import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef, useEffect } from "react";

export const Route = createFileRoute("/app/learning_goal_dialogue")({
  component: RouteComponent,
});

function RouteComponent() {
  const [goal, setGoal] = useState("");
  const [savedGoal, setSavedGoal] = useState("");
  const [context, setContext] = useState("");
  const [step, setStep] = useState(1);
  const editableRef = useRef<HTMLDivElement | null>(null);

  const exampleVocab = [
    { word: "le beau-père", meaning: "stepfather" },
    { word: "se disputer", meaning: "to argue" },
    { word: "éducation", meaning: "upbringing" },
    { word: "parler franchement", meaning: "speak honestly" },
  ];

  useEffect(() => {
    if (editableRef.current && goal === "") {
      editableRef.current.innerText = "";
    }
  }, [goal]);

  return (
    <div className="max-w-xl mx-auto p-4 sm:p-6 space-y-6 font-sans bg-white rounded-xl shadow-md mt-10">
      {step === 1 && (
        <div className="space-y-5 text-left">
          <p className="text-xl sm:text-2xl font-bold text-gray-800">
            🎯 What’s your goal with French?
          </p>
          <p className="text-base text-gray-600 leading-relaxed">
            Tell me what you want to achieve — I’ll help you get there.
          </p>
          <div
            ref={editableRef}
            contentEditable
            suppressContentEditableWarning
            onInput={(e) => setGoal(e.currentTarget.textContent || "")}
            className={`min-h-[100px] w-full border border-gray-300 rounded-xl p-4 text-base focus:outline-none focus:ring-2 focus:ring-blue-400 relative ${
              goal === ""
                ? "before:content-[attr(data-placeholder)] before:text-gray-400 before:absolute before:left-4 before:top-4 before:pointer-events-none"
                : ""
            }`}
            data-placeholder="E.g. I want to order food in a French café... or talk to my grandmother about her childhood"
          />
          <button
            onClick={() => setStep(2)}
            disabled={!goal.trim()}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50"
          >
            Continue
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6 text-left">
          <p className="text-xl sm:text-2xl font-bold text-gray-800">
            🔥 Your goal is saved
          </p>
          <p className="text-gray-700 text-base leading-relaxed">
            “<span className="font-semibold text-gray-900">{goal}</span>” —
            great choice. I’ll help you get there by weaving the right words
            into the texts you’ll read.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            I’ve created a vocabulary list tailored to your goal. You’ll come
            across these words in your reading — so they stay active and
            memorable.
          </p>

          <ul className="list-disc list-outside text-gray-700 pl-6 text-base">
            {exampleVocab.map((item, index) => (
              <li key={index} className="mb-1">
                <span className="font-semibold text-gray-900">{item.word}</span>{" "}
                — {item.meaning}
              </li>
            ))}
          </ul>

          <div>
            <p className="font-medium text-gray-800 mb-1 text-base">
              Want to help me fine-tune your word list even more?
            </p>
            <textarea
              placeholder="E.g. family arguments, childhood memories, emotional talks"
              className="w-full border border-gray-300 rounded-xl p-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={context}
              onChange={(e) => setContext(e.target.value)}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => setSavedGoal(goal)}
              className="bg-green-600 text-white py-3 px-6 rounded-xl font-semibold hover:bg-green-700"
            >
              ✅ Confirm Goal
            </button>
            <button
              onClick={() => setStep(1)}
              className="border border-gray-400 text-gray-800 py-3 px-6 rounded-xl hover:bg-gray-100"
            >
              ✏️ Change Goal
            </button>
          </div>

          {savedGoal && (
            <p className="text-green-600 font-semibold mt-4 text-base">
              ✅ Your goal is set! Let’s start reading — your personalized
              vocabulary will show up in every text.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
